import { Link } from "react-router-dom";
import { Zap, Instagram, ArrowUpRight } from "lucide-react";

const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const socials = [
  { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/derstrombaron/", testId: "social-instagram" },
  { name: "TikTok", icon: TikTokIcon, href: "https://www.tiktok.com/@strombaron", testId: "social-tiktok" },
];

export default function FooterSection() {
  return (
    <footer className="relative bg-brand-inverse text-brand-onInverse overflow-hidden" data-testid="site-footer">
      {/* Massive monument wordmark */}
      <div aria-hidden className="pointer-events-none select-none relative overflow-hidden -mb-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <div
            className="font-heading font-black tracking-[-0.05em] leading-none text-white/[0.04] whitespace-nowrap"
            style={{ fontSize: 'clamp(6rem, 22vw, 20rem)' }}
          >
            STROMBARON
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-2.5 group" aria-label="Zur Startseite" data-testid="footer-logo-link">
              <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center group-hover:bg-brand-green group-hover:border-brand-green transition-colors">
                <Zap className="w-4 h-4" style={{ color: 'var(--brand-green-neon)' }} strokeWidth={2.5} />
              </div>
              <span className="font-heading text-xl font-black tracking-tight text-white">Strombaron</span>
            </Link>
            <p className="mt-5 text-sm text-neutral-300 max-w-md leading-relaxed">
              Ihr digitaler Partner für günstigere Strom- und Gastarife – deutschlandweit,
              für Privathaushalte und Gewerbekunden. Kostenlos, unverbindlich und DSGVO-konform.
            </p>

            {/* Socials inline */}
            <div className="mt-7 flex items-center gap-3" data-testid="footer-socials">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  data-testid={s.testId}
                  className="w-11 h-11 rounded-xl border border-white/15 bg-white/[0.03] text-white flex items-center justify-center hover:bg-brand-green hover:border-brand-green hover:text-brand-ink transition-all"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Rechtliches */}
          <div className="md:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">Rechtliches</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><FooterLink to="/impressum" testId="footer-impressum">Impressum</FooterLink></li>
              <li><FooterLink to="/datenschutz" testId="footer-datenschutz">Datenschutz</FooterLink></li>
              <li><FooterLink to="/karriere" testId="footer-karriere">Karriere</FooterLink></li>
            </ul>
          </div>

          {/* Kontakt */}
          <div className="md:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">Kontakt</p>
            <ul className="mt-5 space-y-2 text-sm text-neutral-300">
              <li className="text-white font-semibold">Strombaron</li>
              <li>Westring 117, 32051 Herford</li>
              <li>
                <a href="mailto:info@strombaron.de" className="link-underline text-white">info@strombaron.de</a>
              </li>
              <li>
                <a href="tel:+491728246564" className="link-underline text-white">0172 824 6564</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-neutral-500">
          <p>© {new Date().getFullYear()} Strombaron. Alle Rechte vorbehalten.</p>
          <p>Made with care in Herford, NRW.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-1.5 text-neutral-300 hover:text-white transition-colors"
      data-testid={testId}
    >
      {children}
      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" strokeWidth={2.4} />
    </Link>
  );
}
